import { makeAutoObservable, runInAction } from "mobx";
import { ChangeEvent } from "react";
import { v4 as uuidv4 } from "uuid";
import { ANALYTICS_EVENTS } from "../../../analytics-store.tsx";
import { BackendError } from "../../../config/axios.tsx";
import { APPS } from "../../../constants/AppsConstants.ts";
import {
	CONTENT_MODERATION_INPUT_ERROR,
	DEFAULT_BACKGROUND_CONFIGS,
	DEFAULT_BRUSH,
	DEFAULT_SIZE_CONFIGS,
	OBJECT_MANIPULATION_CONFIGS,
	PollingFileStatus,
	SegmentationMaskTypes,
	engineExpansionError,
} from "../../../constants/ImageToImageConstants.ts";
import RouterConstants from "../../../constants/RouterConstants.ts";
import { checkImageHasTransparentPixels } from "../../../helpers/images.ts";
import { BrushActions } from "../../../hooks/brush/useBrushActions.tsx";
import useErrorToast from "../../../hooks/useErrorToast.tsx";
import { IRootStore } from "../../../mobx/root-store.tsx";
import { BrushCanvasRefs, IBrushSettings } from "../../../models/brush.ts";
import {
	ImageEditingViewMode,
	ImageToImageConfigType,
	OfflineProps,
	PlaygroundImage,
	PlaygroundResult,
	SelectedConfigsProps,
} from "../../../models/image-to-image.ts";
import { IframePostMessageActionTypes, IframePostMessageItemTypes } from "../../../models/new-iframe.ts";
import { Task } from "../../../models/playground.ts";
import ImagesService from "../../../services/ImagesService.ts";
import QueryService from "../../../utils/QueryService.ts";
import { getCroppedImg } from "../../../utils/cropImage.ts";
import { isFoxApps } from "../../../utils/index.ts";
import useImageUtils from "../../../utils/useImageUtils.tsx";
import iframeStore from "../../IframeNew/iframe-store.tsx";

export interface IImageToImageStore {
	config: ImageToImageConfigType;
	handleConfigChange: <K extends keyof ImageToImageConfigType>(key: K, value: ImageToImageConfigType[K]) => void;
	resetConfigChange: () => void;
	handleUploadImages: (e: ChangeEvent<HTMLInputElement>, type?: any) => Promise<void>;
	generateImageToImage: (
		images?: PlaygroundImage[],
		config?: ImageToImageConfigType,
		isFromUpload?: boolean,
		num_results?: number,
	) => Promise<void>;
	errors: { generate?: BackendError };
	nflRmbgBeforeStyle: (images: PlaygroundImage[]) => Promise<void>;
	mixFaceBody: () => Promise<void>;
	refineColor: () => Promise<void>;
	addAIEditorImageInARow: (imageUrl: string) => Promise<void>;
	MAX_FILES_LIMIT: number;
	MAX_FILE_SIZE: number;
	selected_configs: SelectedConfigsProps;
	getGeneratedImagesCount: () => number;
	handleSelectedConfigsChange: <K extends keyof SelectedConfigsProps>(key: K, value: SelectedConfigsProps[K]) => void;
	abortImageGeneration: () => void;
	abortController?: AbortController;
	isGeneratingImages: boolean;
	isErasingImages: boolean;
	isResizingImages: boolean;
	isEraseConfigsEnabled: boolean;
	lastSelectedImages: PlaygroundImage[];
	toggleEraseConfigs: () => void;
	brushCanvasRefs: BrushCanvasRefs[];
	brushConfigs: IBrushSettings;
	eraseObject: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	activeConfig: string;
	handleActiveConfigChange: (config: string) => void;
	callGenFillApi: (images: PlaygroundImage[], config?: ImageToImageConfigType) => Promise<void>;
	handleResetBrusher: () => void;
	isMaskApplied: boolean;
	toggleIsMaskApplied: (status: boolean) => void;
	isManualPlacementConfigEnabled: boolean;
	toggleManualPlacementConfig: (status: boolean) => void;
	isBackgroundConfigEnabled: boolean;
	toggleBackgroundConfig: (status: boolean) => void;
	expandBackground: (
		image: PlaygroundImage,
		config?: ImageToImageConfigType,
		keep_aspect_ratio_value_in_config?: boolean,
	) => Promise<void>;
	cropImage: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	increaseImageResolution: (
		image: PlaygroundImage,
		resolution: number,
		config?: ImageToImageConfigType,
	) => Promise<void>;
	playgroundResultId: string;
	isDrawing: boolean;
	tempBrushSize?: number;
	getMasks: (imageUrl: string) => Promise<string>;
	// This image is chosen when an error occurs in an image and we want to pick the original image index, thumbnails navigation use it to slide to the index of the image
	thumbnailNavigationSlideToImage: PlaygroundImage | null;
	handleNumResultsChange: (numResults: number) => void;
}

export default class ImageToImageStore implements IImageToImageStore {
	private queryService: QueryService = new QueryService("/image-to-image");
	private imageModificationsService: QueryService = new QueryService("/image_modifications");
	abortController: AbortController = new AbortController();
	rootStore: IRootStore;
	config: ImageToImageConfigType = {
		original_image: { id: "", url: "", playground_result_id: "" },
		num_results: isFoxApps() ? 4 : 1,
		background: DEFAULT_BACKGROUND_CONFIGS,
		size: DEFAULT_SIZE_CONFIGS,
		object_manipulation: OBJECT_MANIPULATION_CONFIGS,
		content_moderation: false,
	};
	thumbnailNavigationSlideToImage: PlaygroundImage | null = null;
	errors: { generate?: BackendError } = {};
	errorToast = useErrorToast();

	MAX_FILE_SIZE = 12 * 1024 * 1024;

	MAX_FILES_LIMIT = 35;

	selected_configs: SelectedConfigsProps = {
		background: [],
		size: [],
		foreground_placement: [],
	};

	isGeneratingImages = false;
	isErasingImages = false;
	isEraseConfigsEnabled = false;
	isMaskApplied = false;
	isManualPlacementConfigEnabled = false;
	isBackgroundConfigEnabled = false;
	isResizingImages = false;
	playgroundResultId = "";
	lastSelectedImages: PlaygroundImage[] = [];
	brushCanvasRefs: BrushCanvasRefs[] = [];
	isDrawing: boolean = false;
	tempBrushSize?: number = undefined;
	brushConfigs = {
		globalCompositeOperation: DEFAULT_BRUSH.globalCompositeOperation,
		strokeStyle: DEFAULT_BRUSH.strokeStyle,
		lineWidth: DEFAULT_BRUSH.lineWidth,
		brushColorOpacity: DEFAULT_BRUSH.brushColorOpacity,
		lineJoin: DEFAULT_BRUSH.lineJoin,
		lineCap: DEFAULT_BRUSH.lineCap,
		globalAlpha: DEFAULT_BRUSH.globalAlpha,
		reset: DEFAULT_BRUSH.reset,
		isMaskApplied: false,
	};
	activeConfig: string = "";

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	handleSelectedConfigsChange = <K extends keyof SelectedConfigsProps>(key: K, value: SelectedConfigsProps[K]) => {
		if (value !== undefined) {
			this.selected_configs[key] = value;
		}
	};

	getGeneratedImagesCount: () => number = () => {
		const { playgroundStore } = this.rootStore;
		return (
			playgroundStore.getSelectedImages().length *
			(this.selected_configs.background?.length || 1) *
			(this.selected_configs.size?.length || 1) *
			(this.selected_configs.foreground_placement?.length || 1) *
			(this.selected_configs.image_reference?.length || 1)
		);
	};

	handleConfigChange = <K extends keyof ImageToImageConfigType>(key: K, value: ImageToImageConfigType[K]) => {
		this.config[key] = value;
	};

	resetConfigChange = () => {
		this.config = {
			original_image: { id: "", url: "", playground_result_id: "" },
			num_results: isFoxApps() ? 4 : 1,
			background: DEFAULT_BACKGROUND_CONFIGS,
			size: DEFAULT_SIZE_CONFIGS,
			object_manipulation: OBJECT_MANIPULATION_CONFIGS,
			content_moderation: false,
		};
	};

	abortImageGeneration = () => {
		if (this.abortController) {
			this.abortController.abort();
			this.rootStore.playgroundStore.removeNotLoadingImages();
			// Re-select the images that were previously selected before the generation
			const lastSelectedImages = this.lastSelectedImages;
			if (lastSelectedImages.length > 0) {
				this.rootStore.playgroundStore.selectImages(lastSelectedImages);
			}
			// Reset playGroundLoadedImages on stop Generation
			this.rootStore.playgroundStore.clearPlayGroundLoadedImagesArray();
		}
	};

	generateImageToImage = async (
		images?: PlaygroundImage[],
		config: ImageToImageConfigType = { ...this.config },
		isFromUpload?: boolean,
		numResults?: number,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const { pullUntilAvailable } = useImageUtils();
		this.abortController = new AbortController();
		const selectedImages: PlaygroundImage[] = images || playgroundStore.getSelectedImages();
		this.lastSelectedImages = selectedImages;
		if (!selectedImages.length) return;
		const savedIndex = isFromUpload
			? playgroundStore.playgroundResults.length - 1
			: playgroundStore.playgroundResults.length;
		runInAction(() => {
			this.isGeneratingImages = true;
		});

		if (!isFromUpload) {
			const playground_result_id = uuidv4();
			this.playgroundResultId = playground_result_id;
			const resultsSkeletons: PlaygroundResult = {
				id: playground_result_id,
				config: { ...config },
				type: playgroundStore.selectedConfig,
				images: Array.from({
					length:
						isFoxApps() || isFromUpload
							? selectedImages.length * config.num_results
							: numResults ?? this.getGeneratedImagesCount(),
				}).map((_) => ({
					id: uuidv4(), // Added ID here to preserve Image component key to avoid re-renders
					url: "",
					loading: true,
					playground_result_id: undefined,
				})),
			};
			playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		}

		let resultImages: PlaygroundImage[] = [];
		playgroundStore.handleSelectSingleImage(null);
		// In bulk, if we generate results for multipel selected images, if one of the selected images
		// violate content moderation input, we don't want to stop the whole oepration just delete the
		// results for the violated image
		const deletedFlags = Array(playgroundStore.playgroundResults[savedIndex].images.length).fill(false);
		for (let index = 0; index < selectedImages.length; index++) {
			const selectedImage = selectedImages[index];
			try {
				const formData = new FormData();
				selectedImage.file && formData.append("file", selectedImage.file);
				const existingVariations = playgroundStore.playgroundResults.flatMap((result) =>
					result.images.filter(
						(img) =>
							img.type === "imageToImage" &&
							(img.config as ImageToImageConfigType).original_image.id === selectedImage.id,
					),
				).length;

				formData.append(
					"config",
					JSON.stringify({
						...config,
						original_image: {
							...selectedImage,
							offline_props: {
								...selectedImage.offline_props,
								variation: existingVariations,
							},
							from_app: playgroundStore.selectedConfig,
						},
					}),
				);
				const prepareRes = await this.queryService.post(
					"/prepare_image_generations_tasks",
					formData,
					{ "Content-Type": "multipart/form-data" },
					{ signal: this.abortController.signal },
				);
				const { original, process_style, tasks } = prepareRes;

				const updateStoreWithResults = (
					finalResults: {
						results: PlaygroundImage[];
						original_url: string;
						task?: Task;
					}[],
					existingVariations: number,
				) => {
					const flattenedResults = finalResults.flatMap((item) => item.results);
					let errorMessage = (flattenedResults?.[0] as any)?.error?.message;
					const original_url: string = finalResults.length > 0 ? finalResults[0].original_url : "";

					if (finalResults.length == 0) {
						this.rootStore.playgroundStore.removeNotLoadingImages();
					}

					for (let i = 0; i < flattenedResults.length; i++) {
						// Check for content moderation error input error
						const generatedImage = flattenedResults[i];
						if (generatedImage.error?.status === 422) {
							this.errors.generate = { message: "content moderation", status: 422 };
							throw new Error(CONTENT_MODERATION_INPUT_ERROR);
						}
					}
					resultImages = resultImages.concat(flattenedResults);
					runInAction(async () => {
						const startIndex = index * config.num_results;

						flattenedResults.forEach((generatedImage: PlaygroundImage, resultIndex: number) => {
							const finalIndex = startIndex + resultIndex;

							const availableIndex = playgroundStore.playgroundResults[savedIndex].images.findIndex(
								(img) => img.loading && img.url === "",
							);

							const targetIndex = availableIndex !== -1 ? availableIndex : finalIndex;

							const newImage = {
								...generatedImage,
								// Uploaded image don't have a preset ID like skeleton loading images
								id: isFromUpload
									? uuidv4()
									: playgroundStore.playgroundResults[savedIndex].images[targetIndex].id,
								...(!isFoxApps() &&
									!isFromUpload &&
									selectedImage?.file && { file: selectedImage.file }),
								loading: errorMessage ? false : true,
								config: {
									...playgroundStore.playgroundResults[savedIndex].config,
									...(!isFromUpload && { original_image: { ...selectedImage, url: original_url } }),
								} as ImageToImageConfigType,
								image_config: finalResults[0]?.task?.image_config ?? null,
								mode:
									playgroundStore.imageEditingViewMode === ImageEditingViewMode.SINGLE
										? "single"
										: "bulk",
								type: isFromUpload ? "upload" : playgroundStore.selectedConfig,
								sessionId: (selectedImage.sessionId ?? original_url).split("?")[0],
								variationNum: existingVariations + resultIndex + 1,
								offline_props: {
									...(selectedImage.offline_props as OfflineProps),
									variation: existingVariations + resultIndex + 1,
								},
								playground_result_id: undefined,
							};

							if (errorMessage) {
								(newImage as any).errorType =
									errorMessage === PollingFileStatus.ZERO_BYTE_IMAGE_ERROR
										? PollingFileStatus.CONTENT_MODERATION
										: PollingFileStatus.UNAVAILABLE;
							}

							playgroundStore.playgroundResults[savedIndex].images[targetIndex] =
								newImage as PlaygroundImage;

							playgroundStore.handleSelectSingleImage(newImage as PlaygroundImage);

							if (isFoxApps()) {
								if (isFromUpload) {
									this.rootStore.analyticsStore.logImageToImageEvent(
										ANALYTICS_EVENTS.IMAGE_UPLOAD_ITI,
										playgroundStore.playgroundResults[savedIndex].images[targetIndex],
									);
								} else {
									this.rootStore.analyticsStore.logImageToImageEvent(
										ANALYTICS_EVENTS.ITI_GENERATE,
										playgroundStore.playgroundResults[savedIndex].images[targetIndex],
									);
								}
							}
						});
						if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
							try {
								const images = playgroundStore.playgroundResults[savedIndex].images;
								const playground_result: PlaygroundResult | undefined =
									await projectsStore.createPlaygroundResult({
										...playgroundStore.playgroundResults[savedIndex],
										images: [],
									});

								if (playground_result) {
									await Promise.allSettled(
										images.map(async (img, imageIndex) => {
											try {
												await pullUntilAvailable(img.url, {
													abortController: this.abortController,
												});

												const updatedResult = await projectsStore.uploadImageToPlaygroundResult(
													{
														...img,
														playground_result_id: playground_result.id,
														sessionId: playground_result.session_id,
													},
												);

												if (updatedResult) {
													runInAction(() => {
														const existingIndex = playgroundStore.playgroundResults[
															savedIndex
														].images.findIndex(
															(i) => i.id === img.id || (i.url === "" && i.loading),
														);

														if (existingIndex !== -1) {
															playgroundStore.playgroundResults[savedIndex].images[
																existingIndex
															] = {
																...updatedResult,
																loading: false,
																image_config:
																	finalResults[0]?.task?.image_config ?? null,
																mode:
																	playgroundStore.imageEditingViewMode ===
																	ImageEditingViewMode.SINGLE
																		? "single"
																		: "bulk",
															};
														}
													});
												}
											} catch (error) {
												console.warn(`Image ${imageIndex} failed to load or upload:`, error);
											}
										}),
									);
								}
							} catch (error) {
								console.error("Failed to create playground result:", error);
							}
						}
					});
				};

				const runGenerateAPI = async (
					configOverrides: Partial<{
						original: boolean;
						tasks: Task[];
						process_style: boolean;
					}>,
				): Promise<{
					results: PlaygroundImage[];
					original_url: string;
				}> => {
					const generateFormData = new FormData();
					if (selectedImage.file) {
						generateFormData.append("file", selectedImage.file);
					}

					generateFormData.append(
						"config",
						JSON.stringify({
							...config,
							original_image: {
								...selectedImage,
								offline_props: {
									...selectedImage.offline_props,
									variation: existingVariations,
								},
								from_app: playgroundStore.selectedConfig,
							},
							...configOverrides,
						}),
					);

					const {
						results,
						original_url,
					}: {
						results: PlaygroundImage[];
						original_url: string;
					} = await this.queryService.post(
						"/",
						generateFormData,
						{ "Content-Type": "multipart/form-data" },
						{ signal: this.abortController.signal },
					);

					return { results, original_url };
				};

				let finalResults: {
					results: PlaygroundImage[];
					original_url: string;
				}[] = [];

				if (original) {
					const res = await runGenerateAPI({
						original: true,
						tasks: [],
						process_style: false,
					});
					finalResults.push(res);
					updateStoreWithResults(finalResults, existingVariations);
				} else if (process_style) {
					const res = await runGenerateAPI({
						original: false,
						tasks: [],
						process_style: true,
					});
					finalResults.push(res);
					updateStoreWithResults(finalResults, existingVariations);
				} else if (tasks?.length > 0) {
					await Promise.all(
						tasks.map(async (task: Task, taskIndex: number) => {
							try {
								const res = await runGenerateAPI({
									original: false,
									tasks: [task],
									process_style: false,
								});

								updateStoreWithResults(
									[
										{
											results: res.results,
											original_url: res.original_url,
											task,
										},
									],
									existingVariations,
								);
							} catch (error: any) {
								console.error(`Task ${taskIndex} failed:`, error);
								if (error.response && error.response.status === 420) {
									const errorData = error.response.data[0].error;

									const fakeResults: {
										results: any[];
										original_url: string;
									}[] = [
										{
											results: [
												{
													id: "error-" + taskIndex,
													url: "",
													error: errorData,
												},
											],
											original_url: "",
										},
									];

									updateStoreWithResults(fakeResults, existingVariations);
								} else if (
									error?.message === CONTENT_MODERATION_INPUT_ERROR ||
									(error.response?.status === 422 &&
										error.response.data?.message === engineExpansionError)
								) {
									const isContentModerationError = error.message === CONTENT_MODERATION_INPUT_ERROR;
									const isExpansionError = !isContentModerationError;

									if (isContentModerationError && selectedImages.length === 1) {
										runInAction(() => {
											this.thumbnailNavigationSlideToImage = selectedImage;
										});
										throw new Error("Image generation failed with status 422 and empty URL");
									}

									if (isExpansionError) {
										runInAction(() => {
											this.thumbnailNavigationSlideToImage = selectedImage;
										});
										this.errors.generate = {
											message: error.response.data?.message ?? "",
											status: 422,
										};
									}

									const originalIndex = index * tasks.length + taskIndex;
									const priorDeletions = deletedFlags.slice(0, originalIndex).filter(Boolean).length;
									const adjustedIndex = originalIndex - priorDeletions;
									playgroundStore.playgroundResults[savedIndex].images.splice(adjustedIndex, 1);
									deletedFlags[originalIndex] = true;
								}
							}
						}),
					);
				}
			} catch (e: any) {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			} finally {
				runInAction(() => {
					this.isGeneratingImages = false;
				});
			}
		}
		if (playgroundStore.playgroundResults[savedIndex].images.length == 0) {
			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== savedIndex,
			);
		}

		if (isFromUpload) {
			iframeStore.sendUploadPostMessage(resultImages);
		} else {
			iframeStore.sendGeneratePostMessage(this.config, resultImages);
		}
	};

	callObjectApi = async (apiRoute: string, imageUrl: string, maskType: string, config?: ImageToImageConfigType) => {
		if (this.brushCanvasRefs.length > 0 && this.brushCanvasRefs[0].canvasRef) {
			const brushActions = new BrushActions(
				this.brushCanvasRefs[0].canvasRef.current,
				this.brushCanvasRefs[0].canvasOverlayRef.current,
			);
			const maskFileBase64 = await brushActions.handleCanvasDownload();
			if (maskFileBase64 && imageUrl) {
				try {
					const formData = new FormData();
					formData.append("imageUrl", imageUrl);
					formData.append("maskFile", maskFileBase64);
					formData.append("maskType", maskType);
					formData.append("sync", "false");
					formData.append("content_moderation", String(this.config.content_moderation));
					this.handleConfigChange("object_manipulation", {
						...this.config.object_manipulation,
						mask: maskFileBase64,
					});

					if (config) {
						const img = new Image();
						img.src = imageUrl;

						await new Promise<void>((resolve, reject) => {
							img.onload = () => {
								formData.append("mask_width", String(img.width));
								formData.append("mask_height", String(img.height));
								formData.append("prompt", config.object_manipulation?.prompt || "");
								formData.append("num_results", String(config.object_manipulation?.num_results || 0));
								formData.append("content_moderation", String(config.content_moderation));
								resolve();
							};
							img.onerror = (err) => reject(new Error(`Failed to load image: ${err}`));
						});
					}

					const newImageUrl = await this.imageModificationsService.post(apiRoute, formData, {
						"Content-Type": "multipart/form-data",
					});
					return { newImageUrl, maskFileBase64 };
				} catch (e) {
					throw new Error(`Error1 erasing object ${e}`);
				} finally {
					this.toggleIsMaskApplied(false);
				}
			}
		}
	};

	eraseObject = async (image: PlaygroundImage, config: ImageToImageConfigType = this.config): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const savedIndex = playgroundStore.playgroundResults.length;

		runInAction(() => {
			this.isErasingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		try {
			const maskType = config.object_manipulation?.mask_type || SegmentationMaskTypes.MANUAL;
			const res = await this.callObjectApi("/eraser", image.url, maskType);

			if (!res?.newImageUrl) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			} else if (res) {
				const { newImageUrl, maskFileBase64 } = res;
				const { playgroundResults, projectsStore } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: newImageUrl,
					loading: true,
					type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
						? APPS.AUTOMOTIVE
						: APPS.IMAGE_TO_IMAGE,
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
						size: {},
					} as ImageToImageConfigType,
					image_config: {
						object_manipulation: {
							...this.config?.object_manipulation,
							erase: true,
						},
					} as ImageToImageConfigType,
					mode: "single",
				};

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);

				iframeStore.sendActionPostMessage(IframePostMessageActionTypes.EraseObject, {
					input: {
						image_src: newImageUrl,
						mask_src: maskFileBase64,
					},
					results: [
						{
							type: IframePostMessageItemTypes.Image,
							src: newImageUrl,
						},
					],
				});
				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			} else {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error erasing object`);
				});
			}
		} catch (e: any) {
			if (e?.message.includes("422")) {
				runInAction(() => {
					this.thumbnailNavigationSlideToImage = image;
					this.errors.generate = {
						message: e?.message ?? "",
						status: 422,
					};
				});
			}
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
				throw new Error(`Error erasing object: ${e.message || e.toString()}`);
			});
		} finally {
			runInAction(() => {
				this.isErasingImages = false;
			});
		}
	};

	mixFaceBody = async (): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const selectedImages: PlaygroundImage[] = playgroundStore.getSelectedImages();
		const isMixEnabled =
			selectedImages.length === 2 &&
			selectedImages.every(
				(image) =>
					image.type === APPS.IMAGE_TO_IMAGE &&
					(image.config as ImageToImageConfigType)?.original_image.id ===
						(selectedImages[0].config as ImageToImageConfigType)?.original_image.id,
			);
		if (!isMixEnabled) return;
		const savedIndex = playgroundStore.playgroundResults.length;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.config, num_results: 1 },
			type: APPS.IMAGE_TO_IMAGE,
			images: Array.from({ length: 2 }).map((_) => ({
				id: "",
				url: "",
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		Array.from({ length: 2 }).forEach(async (_, index) => {
			try {
				const formData = new FormData();
				this.handleConfigChange(
					"original_image",
					(selectedImages[index].config as ImageToImageConfigType).original_image,
				);
				const faceImage = selectedImages[index % 2];
				const bodyImage = selectedImages[(index + 1) % 2];
				formData.append(
					"config",
					JSON.stringify({
						...this.config,
						num_results: 1,
						style: {
							...this.config.style,
							face_seed: faceImage.style_props?.face_seed,
							face_sd_edit: faceImage.style_props?.face_sd_edit,
							face_mask_steps: faceImage.style_props?.face_mask_steps,
							num_inference_steps: faceImage.style_props?.num_inference_steps,
							canny_scale: faceImage.style_props?.canny_scale,
							body_seed: bodyImage.style_props?.body_seed,
							body_sd_edit: bodyImage.style_props?.body_sd_edit,
							logo_enhance: bodyImage.style_props?.logo_enhance,
							box_threshold: bodyImage.style_props?.box_threshold,
							text_threshold: bodyImage.style_props?.text_threshold,
							max_size_height: bodyImage.style_props?.max_size_height,
							max_size_width: bodyImage.style_props?.max_size_width,
							min_size: bodyImage.style_props?.min_size,
						},
					}),
				);
				const { results } = await this.queryService.post("/", formData, {
					"Content-Type": "multipart/form-data",
				});

				runInAction(() => {
					const startIndex = index;
					results.forEach((generatedImage: PlaygroundImage, index: number) => {
						playgroundStore.playgroundResults[savedIndex].images[startIndex + index] = {
							...generatedImage,
							id: uuidv4(),
							loading: true,
							config: {
								...playgroundStore.playgroundResults[savedIndex].config,
								original_image: (selectedImages[index].config as ImageToImageConfigType).original_image,
							} as ImageToImageConfigType,
							type: APPS.IMAGE_TO_IMAGE,
							sessionId: selectedImages[index].sessionId,
						};
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.ITI_MIX_FACE_BODY,
							playgroundStore.playgroundResults[savedIndex].images[startIndex + index],
						);
					});
				});
			} catch (e: any) {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			}
		});
	};

	refineColor = async (): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const selectedImages: PlaygroundImage[] = playgroundStore.getSelectedImages();
		if (!selectedImages.length) return;
		const savedIndex = playgroundStore.playgroundResults.length;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.config, num_results: 1 },
			type: APPS.IMAGE_TO_IMAGE,
			images: Array.from({ length: selectedImages.length }).map((_) => ({
				id: "",
				url: "",
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		Array.from({ length: selectedImages.length }).forEach(async (_, index) => {
			try {
				const formData = new FormData();
				this.handleConfigChange(
					"original_image",
					(selectedImages[index].config as ImageToImageConfigType).original_image,
				);
				formData.append(
					"config",
					JSON.stringify({
						...this.config,
						num_results: 1,
						style: {
							...this.config.style,
							is_normalize_face: true,
							face_seed: selectedImages[index].style_props?.face_seed,
							face_sd_edit: selectedImages[index].style_props?.face_sd_edit,
							num_inference_steps: selectedImages[index].style_props?.num_inference_steps,
							face_mask_steps: selectedImages[index].style_props?.face_mask_steps,
							canny_scale: selectedImages[index].style_props?.canny_scale,
							body_seed: selectedImages[index].style_props?.body_seed,
							body_sd_edit: selectedImages[index].style_props?.body_sd_edit,
							logo_enhance: selectedImages[index].style_props?.logo_enhance,
							box_threshold: selectedImages[index].style_props?.box_threshold,
							text_threshold: selectedImages[index].style_props?.text_threshold,
							max_size_height: selectedImages[index].style_props?.max_size_height,
							max_size_width: selectedImages[index].style_props?.max_size_width,
							min_size: selectedImages[index].style_props?.min_size,
						},
					}),
				);
				const { results } = await this.queryService.post("/", formData, {
					"Content-Type": "multipart/form-data",
				});

				runInAction(() => {
					const startIndex = index;
					results.forEach((generatedImage: PlaygroundImage, index: number) => {
						playgroundStore.playgroundResults[savedIndex].images[startIndex + index] = {
							...generatedImage,
							id: uuidv4(),
							loading: true,
							config: {
								...playgroundStore.playgroundResults[savedIndex].config,
								original_image: (selectedImages[index].config as ImageToImageConfigType).original_image,
							} as ImageToImageConfigType,
							type: APPS.IMAGE_TO_IMAGE,
						};
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.ITI_COLOR_REFINE_IMAGE,
							playgroundStore.playgroundResults[savedIndex].images[startIndex + index],
						);
					});
				});
			} catch (e: any) {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			}
		});
	};

	nflRmbgBeforeStyle = async (images: PlaygroundImage[]): Promise<void> => {
		await this.generateImageToImage(
			images,
			{
				original_image: { id: "", url: "", playground_result_id: "" },
				background: { remove: true, is_fox: true },
				num_results: 1,
			},
			true,
		);
	};

	uploadOriginalImage = async (images: PlaygroundImage[]): Promise<void> => {
		await this.generateImageToImage(
			images,
			{
				original_image: { id: "", url: "", playground_result_id: "" },
				background: { original: true },
				num_results: 1,
			},
			true,
		);
	};

	handleUploadImages = async (e: ChangeEvent<HTMLInputElement>, type: any = "upload") => {
		const files: File[] | null = Array.from(e.target.files || []);
		e.target.value = "";

		if (files.length) {
			const { playgroundStore } = this.rootStore;
			const resultsSkeletons: PlaygroundResult = {
				id: uuidv4(),
				config: { ...this.config },
				type: type,
				images: Array.from({
					length: files.length,
				}).map((_) => ({
					id: "",
					url: "",
					loading: true,
					playground_result_id: "",
				})),
			};

			playgroundStore.playgroundResults = await [...playgroundStore.playgroundResults, resultsSkeletons];

			// const compressedFiles: File[] = await Promise.all(
			// 	files.map(async (file: File) => {
			// 		const compressedBlob = await imageCompression(file, IMAGE_COMPRESSION_CONFIG);
			// 		const compressedFile = new File([compressedBlob], file.name, { type: compressedBlob.type });
			// 		return compressedFile;
			// 	}),
			// );
			const imagesBeforeUpload: PlaygroundResult = {
				id: uuidv4(),
				config: { ...this.config },
				type: type,
				images: await Promise.all(
					files.map(async (file) => {
						return {
							id: uuidv4(),
							url: URL.createObjectURL(file),
							file: file,
							type: type,
							offline_props: {
								prefix_id: file.name.split(".")[0],
								variation: 0,
							},
							playground_result_id: "",
						};
					}),
				),
			};

			isFoxApps()
				? await this.nflRmbgBeforeStyle(imagesBeforeUpload.images)
				: await this.uploadOriginalImage(imagesBeforeUpload.images);

			runInAction(async () => {
				this.rootStore.playgroundStore.selectImages(imagesBeforeUpload.images);
			});
		}
	};

	addAIEditorImageInARow = async (imageUrl: string) => {
		const response = await fetch(imageUrl);
		const blob = await response.blob();
		const filename = imageUrl.substring(imageUrl.lastIndexOf("/") + 1);
		const { playgroundStore } = this.rootStore;

		const analyticsUploadedImageUrl = new URL(imageUrl);
		const analyticsUploadedImageId = analyticsUploadedImageUrl.searchParams.get("imageId");
		const imagesBeforeUpload: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.config },
			type: "save",
			images: [
				{
					id: uuidv4(),
					url: imageUrl,
					file: new File([blob], filename, { type: blob.type }),
					type: APPS.IMAGE_TO_IMAGE,
					config: { ...this.config },
					sessionId:
						imageUrl.split("?")[0] +
						(analyticsUploadedImageId ? `?imageId=${analyticsUploadedImageId}` : ""),
					playground_result_id: "",
				},
			],
		};

		if (analyticsUploadedImageId) {
			this.rootStore.analyticsStore.logImageToImageEvent(
				ANALYTICS_EVENTS.PLAYGROUND_IMAGE_SAVE_ITI,
				imagesBeforeUpload.images[0],
			);
		} else {
			const existingItiImage = playgroundStore.getAvailableImages().find((image) => imageUrl === image?.url);
			if (existingItiImage) {
				this.rootStore.analyticsStore.logImageToImageEvent(
					ANALYTICS_EVENTS.PLAYGROUND_IMAGE_SAVE_ITI,
					existingItiImage,
				);
			}
		}

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, imagesBeforeUpload];
	};

	handleActiveConfigChange = (config: string) => {
		runInAction(() => {
			this.activeConfig = config;
		});
	};

	toggleEraseConfigs = () => {
		runInAction(() => {
			this.isEraseConfigsEnabled = !this.isEraseConfigsEnabled;
		});
	};

	callGenFillApi = async (
		images?: PlaygroundImage[],
		config: ImageToImageConfigType = { ...this.config },
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		this.abortController = new AbortController();
		const selectedImages: PlaygroundImage[] = images || playgroundStore.getSelectedImages();
		if (!selectedImages.length) return;
		const savedIndex = playgroundStore.playgroundResults.length;
		runInAction(() => {
			this.isErasingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...config },
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			images: Array.from({
				length: config.object_manipulation?.num_results || 0,
			}).map((_) => ({
				id: "",
				url: "",
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];

		for (let index = 0; index < selectedImages.length; index++) {
			const selectedImage = selectedImages[index];
			try {
				const maskType = config.object_manipulation?.mask_type || SegmentationMaskTypes.MANUAL;
				const response = await this.callObjectApi("/gen_fill", selectedImage.url, maskType, config);
				if (!response) {
					continue;
				}

				if (!response || !response.newImageUrl || response.newImageUrl.length === 0) {
					this.rootStore.playgroundStore.removeNotLoadingImages();
					continue;
				}
				const { newImageUrl, maskFileBase64 } = response;

				if (newImageUrl?.length == 0) {
					this.rootStore.playgroundStore.removeNotLoadingImages();
				}

				runInAction(() => {
					const startIndex = index * config.num_results;

					newImageUrl?.forEach((generatedImage: any, resultIndex: number) => {
						const newImageUrl = generatedImage;
						const finalIndex = startIndex + resultIndex;

						const newImage = {
							...selectedImage,
							...(selectedImage.file ? { file: undefined } : {}),
							id: uuidv4(),
							url: newImageUrl,
							loading: true,
							type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
								? APPS.AUTOMOTIVE
								: APPS.IMAGE_TO_IMAGE,
							config: {
								...config,
								original_image: { ...selectedImage },
								size: {},
							} as ImageToImageConfigType,
							image_config: {
								object_manipulation: {
									...this.config?.object_manipulation,
									gen_fill: true,
								},
							} as ImageToImageConfigType,
							mode: "single",
						};

						const availableIndex = playgroundStore.playgroundResults[savedIndex].images.findIndex(
							(img) => img.loading && img.id === "" && img.url === "",
						);

						const targetIndex = availableIndex !== -1 ? availableIndex : finalIndex;

						playgroundStore.handleSelectSingleImage(newImage);

						playgroundStore.playgroundResults[savedIndex].images[targetIndex] = newImage;

						iframeStore.sendActionPostMessage(IframePostMessageActionTypes.EraseObject, {
							input: {
								image_src: newImageUrl,
								mask_src: maskFileBase64,
							},
							results: [
								{
									type: IframePostMessageItemTypes.Image,
									src: newImageUrl,
								},
							],
						});
					});
					if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
						projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
					}
				});
			} catch (e: any) {
				if (e?.message.includes("422")) {
					runInAction(() => {
						this.thumbnailNavigationSlideToImage = selectedImage;
						this.errors.generate = {
							message: e?.message ?? "",
							status: 422,
						};
					});
				}
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			} finally {
				runInAction(() => {
					this.isErasingImages = false;
				});
			}
		}
	};

	handleResetBrusher = () => {
		if (this.brushCanvasRefs.length > 0) {
			const canvasRef = this.brushCanvasRefs[0]?.canvasRef.current;
			const overlayCanvasRef = this.brushCanvasRefs[0]?.canvasOverlayRef.current;

			if (overlayCanvasRef) {
				const brushActions = new BrushActions(canvasRef, overlayCanvasRef);

				// Clear the canvas
				brushActions.handleCanvasClear();

				// Update the reset flag in the store
				runInAction(() => {
					this.brushConfigs.reset = false;
				});
			}
		}
	};

	toggleIsMaskApplied = (status: boolean) => {
		runInAction(() => {
			this.isMaskApplied = status;
		});
	};

	toggleManualPlacementConfig = (status: boolean) => {
		runInAction(() => {
			this.isManualPlacementConfigEnabled = status;
		});
	};

	toggleBackgroundConfig = (status: boolean) => {
		runInAction(() => {
			this.isBackgroundConfigEnabled = status;
		});
	};

	expandBackground = async (
		image: PlaygroundImage,
		config: ImageToImageConfigType = this.config,
		keep_aspect_ratio_value_in_config = false,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const savedIndex = playgroundStore.playgroundResults.length;

		runInAction(() => {
			this.isResizingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		try {
			const formData = new FormData();

			formData.append(
				"config",
				JSON.stringify({
					...config,
					original_image: {
						...image,
						offline_props: {
							...image.offline_props,
						},
					},
				}),
			);
			const result = await this.queryService.post(
				"/expand",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);

			if (!result?.image_res) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			} else if (result) {
				const { image_res } = result;
				const { playgroundResults } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: image_res,
					loading: true,
					type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
						? APPS.AUTOMOTIVE
						: APPS.IMAGE_TO_IMAGE,
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
					} as ImageToImageConfigType,

					image_config: {
						background: {
							expand: true,
						},
					} as ImageToImageConfigType,
					mode: "single",
				};
				if (!keep_aspect_ratio_value_in_config) {
					newImage.config.size = {};
				}

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			} else {
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_, index) => index !== savedIndex,
					);
					throw new Error(`Error erasing object`);
				});
			}
		} catch (e: any) {
			if (e?.message.includes("422") && e.response.data.message !== engineExpansionError) {
				runInAction(() => {
					this.thumbnailNavigationSlideToImage = image;
					this.errors.generate = {
						message: e?.message ?? "",
						status: 422,
					};
				});
			} else if (e?.response?.data?.message === engineExpansionError) {
				this.thumbnailNavigationSlideToImage = image;
				this.errors.generate = {
					message: e.response.data.message ?? "",
					status: 422,
				};
			}
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
				throw new Error(`Error erasing object: ${e.message || e.toString()}`);
			});
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	cropImage = async (image: PlaygroundImage, config: ImageToImageConfigType = this.config): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const savedIndex = playgroundStore.playgroundResults.length;
		const croppedAreaPixels = config.manual_placement?.cropped_area_pixels;

		if (!croppedAreaPixels) {
			return;
		}

		runInAction(() => {
			this.isResizingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		try {
			// Get canvas size and image position for manual placement
			const canvasSize = config.manual_placement?.canvas_size
				? { width: config.manual_placement.canvas_size[0], height: config.manual_placement.canvas_size[1] }
				: undefined;

			const croppedImageUrl = await getCroppedImg(image.url, croppedAreaPixels, canvasSize);

			const hasTransparentPixels = await checkImageHasTransparentPixels(image.url);

			const formData = new FormData();
			const { vhash, seed, style_props, ...sanitizedImage } = image;

			// Create config object
			const configObject = {
				...config,
				manual_placement: {
					...config.manual_placement,
					width: canvasSize?.width,
					height: canvasSize?.height,
				},
				original_image: {
					...sanitizedImage,
					url: croppedImageUrl,
					offline_props: {
						...image.offline_props,
					},
				},
			};

			formData.append("config", JSON.stringify(configObject));

			const result = await this.queryService.post(
				"/crop",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);

			if (!result) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			} else if (result) {
				const { playgroundResults } = playgroundStore;
				const { projectsStore } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: result,
					loading: false,
					type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
						? APPS.AUTOMOTIVE
						: APPS.IMAGE_TO_IMAGE,
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
						...(hasTransparentPixels ? {} : { operation: "crop" }),
						size: {},
					} as ImageToImageConfigType,
					image_config: {
						size: {
							crop: true,
						},
					} as ImageToImageConfigType,
					mode: "single",
				};

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);
				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			}
		} catch (e) {
			console.error(e);
			this.rootStore.playgroundStore.removeNotLoadingImages();
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	increaseImageResolution = async (
		image: PlaygroundImage,
		resolution: number,
		config: ImageToImageConfigType = this.config,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const savedIndex = playgroundStore.playgroundResults.length;

		runInAction(() => {
			this.isResizingImages = true;
		});

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type: APPS.IMAGE_TO_IMAGE,
			images: [
				{
					id: "",
					url: "",
					loading: true,
					selected: true,
					playground_result_id: "",
				},
			],
		};

		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		await playgroundStore.handleSelectSingleImage(null);
		try {
			const scaledUrl = await new ImagesService().increaseResolution(
				image.url,
				resolution,
				config.content_moderation,
			);
			if (!scaledUrl) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
			}
			if (scaledUrl) {
				const { playgroundResults } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = {
					...image,
					...(image.file ? { file: undefined } : {}),
					id: uuidv4(),
					url: scaledUrl,
					loading: true,
					type: APPS.IMAGE_TO_IMAGE,
					mode: "single",
					config: {
						...savedPlaygroundResult.config,
						original_image: { ...image },
						size: {
							increase_resolution: this.config.size?.increase_resolution,
						},
					} as ImageToImageConfigType,
					image_config: {
						size: {
							increase_resolution: this.config.size?.increase_resolution,
						},
					} as ImageToImageConfigType,
				};

				savedPlaygroundResult.images = [newImage];

				playgroundStore.handleSelectSingleImage(newImage);

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			}
		} catch (e: any) {
			if (e?.message.includes("422")) {
				runInAction(() => {
					this.thumbnailNavigationSlideToImage = image;
					this.errors.generate = {
						message: e?.message ?? "",
						status: 422,
					};
				});
			}
			runInAction(() => {
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
				throw new Error(`Error Increasing resolution: ${e.message || e.toString()}`);
			});
		} finally {
			runInAction(() => {
				this.isResizingImages = false;
			});
		}
	};

	getMasks = async (imageUrl: string) => {
		try {
			const formData = new FormData();
			formData.append("image_url", imageUrl);

			const result = await this.queryService.post(
				"/get_masks",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);

			return result?.objects_masks;
		} catch (error) {
			console.log(error);
		}
	};

	handleNumResultsChange = (numResults: number) => {
		this.handleConfigChange("background", {
			...this.config.background,
			generateByText: {
				...this.config.background?.generateByText,
				num_results: numResults,
			},
			generateByImage: {
				...this.config.background?.generateByImage,
				num_results: numResults,
			},
		});
		this.handleConfigChange("object_manipulation", {
			...this.config.object_manipulation,
			num_results: numResults,
		});
	};
}
